import React, { useState, useEffect } from 'react'
import { Info } from 'lucide-react'
import api from 'config/axiosConfig'
import Modal from 'ui/Modal'
import InputField from 'ui/InputField'
import { Team } from 'util/constants'
import { toast } from 'sonner'

interface Props {
    team: Team;
    onClose: () => void;
}

const TEAMNAME_REQUIREMENTS = {
    minLength: 2,
    maxLength: 30
};

const RESTRICTED_TEAMNAME_LIST = ['team', 'societea', 'societea.io', 'societea.com', 'societea.app', 'www.societea.app', 'societeaapp', 'societeainc.com', 'societeainc', 'societea.inc', 'admin', 'administrator', 'partea', 'partea.io', 'partea.com', 'partea.app', 'deleted'];

const TeamnameModal: React.FC<Props> = ({ team, onClose }) => {
    const [teamname, setTeamname] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidTeamname, setIsValidTeamname] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState<string>('');
    const validateTeamname = async (teamname: string): Promise<void> => {
        setIsValidTeamname(true);
        setValidationMessage('');
        
        if (
            /^[a-z0-9_.]{2,30}$/.test(teamname) &&
            !/\.{2,}/.test(teamname) &&
            !teamname.startsWith(".") &&
            !teamname.startsWith("_") &&
            !teamname.endsWith(".") &&
            !teamname.endsWith("_") &&
            !teamname.includes("..") &&
            !teamname.includes("__") &&
            !/^admin/.test(teamname) &&
            !/admin$/.test(teamname) &&
            !RESTRICTED_TEAMNAME_LIST.includes(teamname)
        ) {
            try {
                // Check for existing team with this name
                const teamResponse = await api.get(`/api/team/${teamname}`);
                if (teamResponse.status !== 404) {
                    setIsValidTeamname(false);
                    setValidationMessage('Partea name already taken.');
                    return;
                }

                // Check for existing user with this name
                const userResponse = await api.get(`/api/user/${teamname}`);
                if (userResponse.status !== 404) {
                    setIsValidTeamname(false);
                    setValidationMessage('Partea name conflicts with existing username.');
                    return;
                }

                setIsValidTeamname(true);
                setValidationMessage('');
            } catch (error: any) {
                if (error.response?.status === 404) {
                    setIsValidTeamname(true);
                    setValidationMessage('');
                } else {
                    setIsValidTeamname(false);
                    setValidationMessage('Error validating Partea name.');
                }
            }
        } else {
            if (teamname.startsWith(".") ||
                teamname.startsWith("_") ||
                teamname.endsWith(".") ||
                teamname.endsWith("_")
            ) {
                setIsValidTeamname(false);
                setValidationMessage(`Partea name must not start/end with '.' or '_'`);
            } else if (/^admin/.test(teamname) ||
                /admin$/.test(teamname) ||
                RESTRICTED_TEAMNAME_LIST.includes(teamname)
            ) {
                setIsValidTeamname(false);
                setValidationMessage('Partea name is not allowed.');
            } else if (!/^[a-z0-9_.]{2,30}$/.test(teamname)) {
                setIsValidTeamname(false);
                setValidationMessage(`Partea name may not contain special characters other than '.' and '_'`);
            } else if (teamname.includes("..") ||
                teamname.includes("__")
            ) {
                setIsValidTeamname(false);
                setValidationMessage(`Partea name may not contain consecutive '.' or '_'`);
            }
        }
    };

    useEffect(() => {
        if (teamname && teamname.length > 1) {
            validateTeamname(teamname);
        } else if (teamname && 
            (teamname.startsWith(".") ||
            teamname.startsWith("_"))
        ) {
            setIsValidTeamname(false);
            setValidationMessage(`Partea name must not start with '.' or '_'`);
        } else if (teamname && teamname.length < TEAMNAME_REQUIREMENTS.minLength) {
            setIsValidTeamname(false);
            setValidationMessage(`Partea name must be at least ${TEAMNAME_REQUIREMENTS.minLength} characters long.`);
        } else {
            setValidationMessage('');
        }
    }, [teamname]);

    const handleTeamnameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const lowercaseTeamname = e.target.value.toLowerCase();
        setTeamname(lowercaseTeamname);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);
        try {
            await api.patch(`/api/team/${team.id}/teamname`, { teamname });
            if (team.teamname) {
                team.teamname = teamname;
            }
            toast.success('Partea name updated successfully!');
            window.location.href = `/partea/${teamname}/settings/profile`;
            onClose();
        } catch (error) {
            console.error('Error updating Partea name:', error);
            setValidationMessage('Failed to update Partea name. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = (): void => {
        onClose();
    };

    const isButtonDisabled = (!teamname || !isValidTeamname) ||
        isSubmitting || 
        teamname.length < TEAMNAME_REQUIREMENTS.minLength;

    return (
        <Modal isOpen={true} onClose={handleClose} title="Set your Partea name">
            <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Partea names are publicly visible.
                    </p>

                    <form onSubmit={handleSubmit} className="flex flex-col flex-grow space-y-4">
                        <div className="form-control">
                            <div className="relative">
                                <InputField
                                    type="text"
                                    id="teamname"
                                    name="teamname"
                                    labelText="Partea name"
                                    fieldRequired
                                    placeholder={team.teamname || ''}
                                    value={teamname}
                                    onChange={handleTeamnameChange}
                                    validationState={isValidTeamname && teamname.length >= TEAMNAME_REQUIREMENTS.minLength ? 'valid' : teamname && !isValidTeamname ? 'invalid' : 'none'}
                                    error={validationMessage}
                                    spellCheck={false}
                                    maxLength={TEAMNAME_REQUIREMENTS.maxLength}
                                    minLength={TEAMNAME_REQUIREMENTS.minLength}
                                />

                                {error && (
                                    <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                                        <Info className="mr-1 size-4" />
                                        <span>{error}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </form>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary w-full sm:w-fit py-2 px-4"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={isButtonDisabled}
                        className={`w-full sm:w-fit py-2 px-4 ${
                            isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'
                        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default TeamnameModal; 